import { useState } from "react";
import { z } from "zod";
import { useWatch } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InfoHint } from "@/components/ui/info-hint";
import { useRankedTemplates } from "@/lib/bom-templates";
import type { BomDetail } from "@/lib/bom";
import { LayoutTemplate } from "lucide-react";

// ---------------------------------------------------------------------------
// Zod schema for the header fields
// ---------------------------------------------------------------------------

const bomHeaderSchema = z.object({
  displayProductName: z.string().nullable().optional(),
  decoMethodCode: z.string().nullable().optional(),
  colours: z.number().nullable().optional(),
  positions: z.number().nullable().optional(),
  currencyCode: z.string().min(1),
  currencyExchangeRate: z.number().nullable().optional(),
  quoteNote: z.string().nullable().optional(),
});

type BomHeaderForm = z.infer<typeof bomHeaderSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BomHeaderStripProps {
  bom: BomDetail;
  productId: number | null;
  disabled?: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onApplyTemplate: (templateId: number) => void;
}

// ---------------------------------------------------------------------------
// Helper: convert form values → PATCH body (only header-editable fields)
// ---------------------------------------------------------------------------

function toPatch(values: BomHeaderForm): Record<string, unknown> {
  return {
    displayProductName: values.displayProductName ?? null,
    decoMethodCode: values.decoMethodCode ?? null,
    colours: values.colours ?? null,
    positions: values.positions ?? null,
    currencyCode: values.currencyCode,
    currencyExchangeRate: values.currencyExchangeRate ?? null,
    quoteNote: values.quoteNote ?? null,
  };
}

// ---------------------------------------------------------------------------
// Inner component: rendered inside the EntityForm children render-prop so it
// can call useWatch (a hook) conditionally based on currencyCode.
// ---------------------------------------------------------------------------

interface InnerProps {
  form: UseFormReturn<BomHeaderForm>;
  disabled: boolean;
  productId: number | null;
  onApplyTemplate: (templateId: number) => void;
}

function BomHeaderFields({ form, disabled, productId, onApplyTemplate }: InnerProps) {
  const [showTemplates, setShowTemplates] = useState(false);
  const { data: templates } = useRankedTemplates(productId);

  // Watch currencyCode to conditionally show exchange rate
  const currencyCode = useWatch({ control: form.control, name: "currencyCode" });
  const showExchangeRate = currencyCode !== "AUD";

  // Group templates by displayGroup
  const groupedTemplates = (templates ?? []).reduce<Record<string, typeof templates>>((acc, t) => {
    if (!t) return acc;
    const group = t.displayGroup;
    if (!acc[group]) acc[group] = [];
    acc[group].push(t);
    return acc;
  }, {});

  const handlePickTemplate = (templateId: number) => {
    onApplyTemplate(templateId);
    setShowTemplates(false);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-4">
        {/* Product Name */}
        <div className="col-span-2 md:col-span-8">
          <Label htmlFor="bom-display-product-name" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
            Product name
          </Label>
          <Input
            id="bom-display-product-name"
            {...form.register("displayProductName")}
            disabled={disabled}
            className="h-9 text-sm"
          />
        </div>

        {/* Deco method */}
        <div className="col-span-2 md:col-span-4">
          <Label htmlFor="bom-deco-method" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
            Deco method
            <InfoHint label="Decoration method (e.g. Screen Print, Embroidery). Reference, and drives some pricing." />
          </Label>
          <Input
            id="bom-deco-method"
            {...form.register("decoMethodCode")}
            disabled={disabled}
            className="h-9 text-sm"
          />
        </div>

        {/* Colours */}
        <div className="col-span-1 md:col-span-2">
          <Label htmlFor="bom-colours" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
            Colours
            <InfoHint label="Number of print colours — drives per-colour and price-table pricing." />
          </Label>
          <Input
            id="bom-colours"
            type="number"
            {...form.register("colours", {
              setValueAs: (v: string) => (v === "" ? null : Number(v)),
            })}
            disabled={disabled}
            className="h-9 text-sm w-full"
          />
        </div>

        {/* Positions */}
        <div className="col-span-1 md:col-span-2">
          <Label htmlFor="bom-positions" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
            Positions
            <InfoHint label="Number of decoration positions — drives per-position and price-table pricing." />
          </Label>
          <Input
            id="bom-positions"
            type="number"
            {...form.register("positions", {
              setValueAs: (v: string) => (v === "" ? null : Number(v)),
            })}
            disabled={disabled}
            className="h-9 text-sm w-full"
          />
        </div>

        {/* Currency & Exchange Rate */}
        <div className="col-span-2 md:col-span-5 flex items-start gap-4">
          <div className="w-1/2">
            <Label htmlFor="bom-currency" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
              Currency
              <InfoHint label="Currency of this BOM's costs. Non-AUD lines convert via the exchange rate." />
            </Label>
            <Input
              id="bom-currency"
              {...form.register("currencyCode")}
              disabled={disabled}
              className="h-9 text-sm w-full"
            />
          </div>
          {showExchangeRate && (
            <div className="w-1/2">
              <Label htmlFor="bom-exchange-rate" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
                Exchange rate
                <InfoHint label="Foreign currency units per A$1 — used to convert non-AUD costs." />
              </Label>
              <Input
                id="bom-exchange-rate"
                type="number"
                step="0.0001"
                {...form.register("currencyExchangeRate", {
                  setValueAs: (v: string) => (v === "" ? null : Number(v)),
                })}
                disabled={disabled}
                className="h-9 text-sm w-full"
              />
            </div>
          )}
        </div>

        {/* Change Template button */}
        <div className="col-span-2 md:col-span-3 flex items-end justify-end relative">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => setShowTemplates((prev) => !prev)}
            className="w-full md:w-auto h-9 font-semibold text-xs border-transparent bg-info/15 text-primary hover:bg-info/25"
          >
            <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" />
            Change template
          </Button>

          {showTemplates && (
            <div className="absolute top-full mt-2 right-0 w-72 rounded-xl border border-slate-300 bg-white shadow-xl p-2 z-50 max-h-[300px] overflow-y-auto">
              {Object.keys(groupedTemplates).length === 0 ? (
                <p className="text-sm text-muted-foreground p-4 text-center">No alternative templates available for this product.</p>
              ) : (
                Object.entries(groupedTemplates).map(([group, groupTemplates]) => (
                  <div key={group} className="mb-2 last:mb-0">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-2 py-1.5 bg-slate-100 rounded-md mb-1">
                      {group}
                    </p>
                    <ul className="space-y-0.5">
                      {(groupTemplates ?? []).map((t) => (
                        <li key={t.bomTemplateId}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-100 transition-colors flex flex-col"
                            onClick={() => handlePickTemplate(t.bomTemplateId)}
                            aria-label={t.bomName}
                          >
                            <span className="font-semibold text-slate-800">{t.bomName}</span>
                            {t.bomDesc && (
                              <span className="text-xs text-slate-500 truncate w-full">
                                {t.bomDesc}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Quote Note */}
        <div className="col-span-2 md:col-span-12">
          <Label htmlFor="bom-quote-note" className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
            Quote note
          </Label>
          <Input
            id="bom-quote-note"
            {...form.register("quoteNote")}
            disabled={disabled}
            className="h-9 text-sm"
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BomHeaderStrip — public component
// ---------------------------------------------------------------------------

export function BomHeaderStrip({
  bom,
  productId,
  disabled = false,
  onSave,
  onApplyTemplate,
}: BomHeaderStripProps) {
  const defaultValues: BomHeaderForm = {
    displayProductName: bom.displayProductName,
    decoMethodCode: bom.decoMethodCode,
    colours: bom.colours,
    positions: bom.positions,
    currencyCode: bom.currencyCode,
    currencyExchangeRate: bom.currencyExchangeRate,
    quoteNote: bom.quoteNote,
  };

  return (
    <EntityForm
      schema={bomHeaderSchema}
      defaultValues={defaultValues}
      onSubmit={(values) => onSave(toPatch(values))}
      autosave={(values) => onSave(toPatch(values))}
      autosaveDelayMs={2000}
    >
      {(form) => (
        <BomHeaderFields
          form={form}
          disabled={disabled}
          productId={productId}
          onApplyTemplate={onApplyTemplate}
        />
      )}
    </EntityForm>
  );
}
